import * as _ from "lodash";
import { AbstractApiModel } from "./abstract-api-model";

export interface ExplorerPartHighlightStep {
  instruction: string;
  parts: string[];
}

export type ConfluenceArticle = string;
export class ExplorerPartDetails {
  description = "";
  commands: string[] = [];
  services: string[] = [];
  uses: {
    image_capture: boolean;
    navigation: boolean;
    localization: boolean;
    obstacle_detection: boolean;
    human_detection: boolean;
  } = {
    image_capture: false,
    navigation: false,
    localization: false,
    obstacle_detection: false,
    human_detection: false
  };
  calibration_steps: ExplorerPartHighlightStep[] = [];
  calibration_articles: ConfluenceArticle[];
  replacement_steps: ExplorerPartHighlightStep[] = [];
  replacement_articles: ConfluenceArticle[];
  common_issues: {
    issueName: string;
    steps: ExplorerPartHighlightStep[];
    articles: ConfluenceArticle[];
  }[] = [];

  public getUsesAsStringArray(): string[] {
    const usesAsArray = [];
    this.uses.image_capture && usesAsArray.push("Image Capture");
    this.uses.navigation && usesAsArray.push("Navigation");
    this.uses.localization && usesAsArray.push("Localization");
    this.uses.obstacle_detection && usesAsArray.push("Obstacle Detection");
    this.uses.human_detection && usesAsArray.push("Human Detection");
    return usesAsArray;
  }

  public setUsesFromStringArray(uses: string[]): void {
    this.uses = {
      image_capture: uses.includes("Image Capture"),
      navigation: uses.includes("Navigation"),
      localization: uses.includes("Localization"),
      obstacle_detection: uses.includes("Obstacle Detection"),
      human_detection: uses.includes("Human Detection"),
    };
  }

  constructor(json: any) {
    Object.assign(this, json ?? {});
  }
}

export class RobotPart implements AbstractApiModel<RobotPart> {
  id: number;
  revision: number;
  createdAt: Date;
  updatedAt: Date;
  explorerModel: number;
  fru: string;
  part_aliases?: string[];
  part_model_name: string;
  sys_check?: string[]

  details: ExplorerPartDetails;

  // Creates a copy
  copy(): RobotPart {
    const newPart = new RobotPart();
    newPart.deserialize(this.serialize());
    return newPart;
  }

  serialize() {
    return {
      id: this.id,
      revision: this.revision,
      created_at: this.createdAt,
      updated_at: this.updatedAt,
      explorer_model: this.explorerModel,
      details: this.details,
      fru: this.fru,
      part_aliases: this.part_aliases,
      part_model_name: this.part_model_name,
      sys_check: this.sys_check
    }
  }
  deserialize(json: any): this {
    this.id = json.id && +json.id;
    this.revision = json.revision && +json.revision;
    this.createdAt = json.created_at && new Date(json.created_at);
    this.updatedAt = json.updated_at && new Date(json.updated_at);
    this.explorerModel = json.explorer_model;
    this.details = new ExplorerPartDetails(json.details);
    this.fru = json.fru;
    this.part_aliases = json.part_aliases;
    this.part_model_name = json.part_model_name;
    this.sys_check = json.sys_check;
    return this;
  }
  assign(params: any): this {
    return Object.assign(this, params);
  }
  sortValue(): number {
    return this.id;
  }
  nameCloseness(query: string): number {
    if (this.part_aliases.find(value => value == query)) return 6;
    if (this.part_model_name == query) return 5;
    if (this.part_aliases.find(value => value.startsWith(query))) return 4;
    if (this.part_model_name.startsWith(query)) return 3;
    if (this.part_aliases.find(value => value.includes(query))) return 2;
    if (this.part_model_name.includes(query)) return 1;
    return -1;
  }

  static naturalSort = (a: string, b: string, delimiter = ' ') => {
    const splitA = a.split(delimiter);
    const splitB = b.split(delimiter);
    for (let i = 0; i < _.min([splitA.length, splitB.length]); i++) {
      const compareA = parseInt(splitA[i], 10) || splitA[i].toLowerCase();
      const compareB = parseInt(splitB[i], 10) || splitB[i].toLowerCase();
      if (compareA < compareB) {
      return -1;
      }
      if (compareA > compareB) {
      return 1;
      }
    }

    return 0;
    };
}